import { TixologiResumedEvent } from "@punchup/data/legacy/tixologi";
import type { VenuePageTimezones } from "@punchup/data/models/venue_pages";
import { ShowWithComedian } from "@punchup/data/legacy/shows";
import { updateTixologiAvailabilityServer } from "@punchup/data/legacy";
import dayjs from "@punchup/utils/dayjs";
import {
  ShowGroupLineupMember,
  lineupsAreIdentical,
} from "../client/components/show-groups/utils";
import { upcomingShowsCutoffInstant } from "./upcomingShowsCutoff";

/**
 * Shared post-processing for multi-show landing pages (the show-group page
 * and the email featured-shows page): both resolve a set of Tixologi events
 * (or synthesized equivalents) and must shape them identically so a show
 * renders the same wherever it appears.
 */

/**
 * Tixologi returns recurring events as a single record with a `days[]` array
 * listing individual performance dates. Expand each event into one
 * pseudo-event per day so the consumer page renders one card per performance
 * (using the day's real start/end date and id).
 */
export function expandEventDays(
  events: TixologiResumedEvent[]
): TixologiResumedEvent[] {
  const expanded: TixologiResumedEvent[] = [];
  for (const ev of events) {
    const days = Array.isArray(ev.days) ? ev.days : [];
    if (days.length > 1) {
      for (const day of days) {
        expanded.push({
          ...ev,
          start_date: day.start_date,
          end_date: day.end_date,
          start_date_id: day.id,
          end_date_id: day.id,
        });
      }
    } else {
      expanded.push(ev);
    }
  }
  return expanded;
}

/**
 * Collapse cards that share the same (name, start_date) — Tixologi can still
 * emit multiple records per instance (e.g. one per ticket release) which
 * would otherwise render as duplicate cards. Prefer the record with the most
 * ticket_types populated so price/sold-out info is preserved.
 */
export function dedupeEventsByNameAndDate(
  events: TixologiResumedEvent[]
): TixologiResumedEvent[] {
  const byKey = new Map<string, TixologiResumedEvent>();
  for (const ev of events) {
    const key = `${ev.name.trim().toLowerCase()}::${ev.start_date}`;
    const existing = byKey.get(key);
    if (
      !existing ||
      (ev.ticket_types?.length ?? 0) > (existing.ticket_types?.length ?? 0)
    ) {
      byKey.set(key, ev);
    }
  }
  return Array.from(byKey.values());
}

/**
 * Keep events from the venue's local start-of-today onward (earliest across
 * locations; legacy now-24h when tz data is missing) — the same boundary the
 * grouped grid uses, so a card's count and its drill-down agree. Unlike
 * `comedian_shows.datetime`, Tixologi's `start_date` is a UTC-rendered
 * instant, so the comparison uses the instant form of the cutoff. Sorts
 * ascending by start.
 */
export function filterAndSortUpcomingEvents(
  events: TixologiResumedEvent[],
  timezones: VenuePageTimezones
): TixologiResumedEvent[] {
  const dateCutoff = upcomingShowsCutoffInstant(timezones);
  return events
    .filter((ev) => !dayjs(ev.start_date).isBefore(dateCutoff))
    .sort(
      (a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf()
    );
}

/**
 * Authoritative sold-out + waitlist eligibility, keyed by Tixologi event id.
 * Runs the same Tixologi availability overlay the venue page (grid/carousel)
 * uses — is_sold_out = manual Supabase flag OR live bulk-availability — so
 * the landing pages agree with the grid instead of guessing from the resumed
 * event's ticket tiers (`isAllSoldOut`), which misses hidden tiers and the
 * manual lever. Shows without a Tixologi id are skipped (their availability
 * has no live source; callers overlay the manual flag themselves).
 */
export async function buildTixologiAvailabilityMaps(
  shows: ShowWithComedian[]
): Promise<{
  soldOutByEventId: Record<string, boolean>;
  waitlistShows: Record<string, ShowWithComedian>;
}> {
  const soldOutByEventId: Record<string, boolean> = {};
  const waitlistShows: Record<string, ShowWithComedian> = {};
  const overlaidShows = await updateTixologiAvailabilityServer(shows);
  for (const show of overlaidShows) {
    const eventId = show.tixologi_event_id;
    if (!eventId) continue;
    soldOutByEventId[eventId] = !!show.is_sold_out;
    // The waitlist needs a sold-out show with a venue_id (the WaitlistButton
    // owner); the overlaid `is_sold_out` is the same value as soldOutByEventId.
    if (show.is_sold_out && show.venue_id) {
      waitlistShows[eventId] = show;
    }
  }
  return { soldOutByEventId, waitlistShows };
}

/**
 * Per-event lineups keyed by event id (stringified), plus the shared lineup
 * when every event has an identical non-empty one — the page then renders a
 * single "Lineup" container instead of one per card.
 */
export function buildEventLineups(
  events: TixologiResumedEvent[],
  eventPageIds: Record<string, string>,
  lineupByShowId: Map<string, ShowGroupLineupMember[]>
): {
  eventLineups: Record<string, ShowGroupLineupMember[]>;
  sharedLineup: ShowGroupLineupMember[] | null;
} {
  const eventLineups: Record<string, ShowGroupLineupMember[]> = {};
  for (const ev of events) {
    const showId = eventPageIds[String(ev.id)];
    eventLineups[String(ev.id)] = showId
      ? (lineupByShowId.get(showId) ?? [])
      : [];
  }

  const lineupLists = events.map((ev) => eventLineups[String(ev.id)]);
  const sharedLineup = lineupsAreIdentical(lineupLists) ? lineupLists[0] : null;

  return { eventLineups, sharedLineup };
}
